import { useEffect, useMemo, useRef, useState } from 'react';
import isEqual from 'react-fast-compare';

export interface SelectOption<T> {
  value: T;
  label?: string;
}

/**
 * Manages async-loaded select options with memoization to prevent unnecessary rerenders.
 * Handles undefined options (loading state) and maintains selection validity.
 * Accepts an optional defaultValue, allowing consumers to pass in just the value part of the option.
 */
export function useAsyncSelect<T, Option extends SelectOption<T>>(
  options: Option[] | undefined,
  defaultValue?: T
) {
  const [selectedValue, setSelectedValue] = useState<Option | undefined>(undefined);
  const prevOptionsRef = useRef<Option[] | undefined>(undefined);
  const prevDefaultValueRef = useRef<T | undefined>(defaultValue);

  // Memoize options to maintain referential equality even if caller doesn't
  const memoizedOptions = useMemo(() => {
    if (!options) return undefined;
    if (prevOptionsRef.current && isEqual(options, prevOptionsRef.current)) {
      return prevOptionsRef.current;
    }
    prevOptionsRef.current = options;
    return options;
  }, [options]);

  // Reset selectedValue if options change and selected value isn't valid.
  useEffect(() => {
    if (!memoizedOptions?.length) {
      // No options, means we shouldn't have a selected value.
      setSelectedValue(undefined);
      return;
    }
    // Ensure selectedValue is valid within the current options
    const isSelectedValueValid =
      selectedValue && memoizedOptions.some((option) => isEqual(option, selectedValue));

    if (!isSelectedValueValid) {
      setSelectedValue(undefined);
    }
  }, [memoizedOptions, selectedValue]);

  const defaultOption = memoizedOptions?.find((option) => isEqual(option.value, defaultValue));
  const defaultValueChanged = !isEqual(defaultValue, prevDefaultValueRef.current);
  // Handle defaultValue changes
  useEffect(() => {
    // We don't have a selected value and we have a default option, set it and return.
    if (selectedValue === undefined && defaultOption) {
      setSelectedValue(defaultOption);
      return;
    }
    // Default value changed, discard the selected value, and set the new default option.
    if (defaultValueChanged) {
      setSelectedValue(defaultOption);
    }
    // Update the ref for the next render
    prevDefaultValueRef.current = defaultValue;
  }, [defaultOption, defaultValue, defaultValueChanged, selectedValue]);

  return {
    selectedValue,
    setSelectedValue,
    options: memoizedOptions || [],
    isLoading: memoizedOptions === undefined,
  };
}
